/**
 * Tenant Service
 * Tenant (kiracı/müşteri) işlemleri için API servisi
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS } from '../config/endpoints';
import {
  // Request types
  TenantCreateRequest,
  TenantUpdateRequest,
  TenantListRequest,
  CreateRequest,
  // Response types  
  TenantResponse,
  TenantStatisticsResponse,
  
  // Common types
  ID,
  ApiResponse,
  PaginatedResponse,
  BaseQueryParams,
  ExportFormat
} from '../types';

/**
 * Tenant service interface
 */
export interface ITenantService {
  // CRUD Operations
  getTenants(params?: TenantListRequest): Promise<PaginatedResponse<TenantResponse>>;
  getTenantById(id: ID): Promise<ApiResponse<TenantResponse>>;
  createTenant(data: TenantCreateRequest): Promise<ApiResponse<TenantResponse>>;
  updateTenant(id: ID, data: TenantUpdateRequest): Promise<ApiResponse<TenantResponse>>;
  deleteTenant(id: ID): Promise<ApiResponse<void>>;
  
  // Tenant Management
  activateTenant(id: ID): Promise<ApiResponse<TenantResponse>>;
  deactivateTenant(id: ID): Promise<ApiResponse<TenantResponse>>;
  suspendTenant(id: ID): Promise<ApiResponse<TenantResponse>>;
  resumeTenant(id: ID): Promise<ApiResponse<TenantResponse>>;
  
  // Statistics & Analytics
  getTenantStatistics(tenantId: ID): Promise<ApiResponse<TenantStatisticsResponse>>;
  getTenantDashboard(tenantId: ID): Promise<ApiResponse<any>>;
  
  // Billing & Subscription
  getBillingInfo(tenantId: ID): Promise<ApiResponse<any>>;
  updateBillingInfo(tenantId: ID, data: any): Promise<ApiResponse<any>>;
  getSubscription(tenantId: ID): Promise<ApiResponse<any>>;
  updateSubscription(tenantId: ID, plan: string): Promise<ApiResponse<any>>;
  
  // Users & Access
  getTenantUsers(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>>;
  addUserToTenant(tenantId: ID, userId: ID, role: string): Promise<ApiResponse<void>>;
  removeUserFromTenant(tenantId: ID, userId: ID): Promise<ApiResponse<void>>;
  updateUserRole(tenantId: ID, userId: ID, role: string): Promise<ApiResponse<void>>;
  
  // Settings & Configuration
  getTenantSettings(tenantId: ID): Promise<ApiResponse<any>>;
  updateTenantSettings(tenantId: ID, settings: any): Promise<ApiResponse<any>>;
  resetTenantSettings(tenantId: ID): Promise<ApiResponse<any>>;
  
  // Export Operations
  exportTenantData(tenantId: ID, format: ExportFormat, filters?: any): Promise<Blob>;
  exportTenantReport(tenantId: ID, format: ExportFormat): Promise<Blob>;
  
  // Utility Methods
  searchTenants(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<TenantResponse>>;
  validateTenantDomain(domain: string): Promise<ApiResponse<{ valid: boolean; message?: string }>>;
  checkTenantAvailability(name: string, domain: string): Promise<ApiResponse<{ available: boolean; suggestions?: string[] }>>;
}

/**
 * Tenant Service Implementation
 */
class TenantService implements ITenantService {
  // ==================== CRUD OPERATIONS ====================
  
  async getTenants(params?: TenantListRequest): Promise<PaginatedResponse<TenantResponse>> {
    return apiClient.getList<TenantResponse>(ENDPOINTS.TENANTS.LIST, params, {
      cacheKey: `tenants_${JSON.stringify(params || {})}`,
      cacheTTL: 300000 // 5 dakika cache
    });
  }
  
  async getTenantById(id: ID): Promise<ApiResponse<TenantResponse>> {
    return apiClient.get<TenantResponse>(ENDPOINTS.TENANTS.DETAIL(id), id, {
      cacheKey: `tenant_${id}`,
      cacheTTL: 300000 // 5 dakika cache
    });
  }
  
  async createTenant(data: TenantCreateRequest): Promise<ApiResponse<TenantResponse>> {
    // Cache'i temizle
    apiClient.cancelRequest('tenants_cache');
    const createRequest: CreateRequest<TenantResponse> = {
      data: data as Omit<TenantResponse, 'id' | 'createdAt' | 'updatedAt'>
    };
    
    return apiClient.create<TenantResponse>(ENDPOINTS.TENANTS.CREATE, createRequest);
  }
  
  async updateTenant(id: ID, data: TenantUpdateRequest): Promise<ApiResponse<TenantResponse>> {
    // Cache'i temizle
    apiClient.cancelRequest('tenants_cache');
    apiClient.clearCache(`tenant_${id}`);
    
    return apiClient.update<TenantResponse>(ENDPOINTS.TENANTS.BASE, {
      id,
      data
    });
  }
  
  async deleteTenant(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.cancelRequest('tenants_cache');
    apiClient.clearCache(`tenant_${id}`);
    
    return apiClient.delete(ENDPOINTS.TENANTS.BASE, id);
  }
  
  // ==================== TENANT MANAGEMENT ====================
  
  async activateTenant(id: ID): Promise<ApiResponse<TenantResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_${id}`);
    
    return apiClient.customPatch<TenantResponse>(
      `${ENDPOINTS.TENANTS.BASE}/${id}/activate`,
      {}
    );
  }
  
  async deactivateTenant(id: ID): Promise<ApiResponse<TenantResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_${id}`);
    
    return apiClient.customPatch<TenantResponse>(
      `${ENDPOINTS.TENANTS.BASE}/${id}/deactivate`,
      {}
    );
  }
  
  async suspendTenant(id: ID): Promise<ApiResponse<TenantResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_${id}`);
    
    return apiClient.customPatch<TenantResponse>(
      `${ENDPOINTS.TENANTS.BASE}/${id}/suspend`,
      {}
    );
  }
  
  async resumeTenant(id: ID): Promise<ApiResponse<TenantResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_${id}`);
    
    return apiClient.customPatch<TenantResponse>(
      `${ENDPOINTS.TENANTS.BASE}/${id}/resume`,
      {}
    );
  }
  
  // ==================== STATISTICS & ANALYTICS ====================
  
  async getTenantStatistics(tenantId: ID): Promise<ApiResponse<TenantStatisticsResponse>> {
    return apiClient.customGet<TenantStatisticsResponse>(
      ENDPOINTS.TENANTS.STATISTICS(tenantId),
      {
        cacheKey: `tenant_stats_${tenantId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async getTenantDashboard(tenantId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/dashboard`,
      {
        cacheKey: `tenant_dashboard_${tenantId}`,
        cacheTTL: 60000 // 1 dakika cache (real-time data)
      }
    );
  }
  
  // ==================== BILLING & SUBSCRIPTION ====================
  
  async getBillingInfo(tenantId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/billing`,
      {
        cacheKey: `tenant_billing_${tenantId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async updateBillingInfo(tenantId: ID, data: any): Promise<ApiResponse<any>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_billing_${tenantId}`);
    
    return apiClient.customPut<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/billing`,
      data
    );
  }
  
  async getSubscription(tenantId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/subscription`,
      {
        cacheKey: `tenant_subscription_${tenantId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async updateSubscription(tenantId: ID, plan: string): Promise<ApiResponse<any>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_subscription_${tenantId}`);
    
    return apiClient.customPut<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/subscription`,
      { plan }
    );
  }
  
  // ==================== USERS & ACCESS ====================
  
  async getTenantUsers(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    return apiClient.getList<any>(
      ENDPOINTS.TENANTS.BASE,
      params,
      {
        cacheKey: `tenant_users_${tenantId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async addUserToTenant(tenantId: ID, userId: ID, role: string): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_users_${tenantId}`);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/users/${userId}`,
      { role }
    );
  }
  
  async removeUserFromTenant(tenantId: ID, userId: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_users_${tenantId}`);
    
    return apiClient.customDelete<void>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/users/${userId}`
    );
  }
  
  async updateUserRole(tenantId: ID, userId: ID, role: string): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_users_${tenantId}`);
    
    return apiClient.customPut<void>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/users/${userId}/role`,
      { role }
    );
  }
  
  // ==================== SETTINGS & CONFIGURATION ====================
  
  async getTenantSettings(tenantId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      ENDPOINTS.TENANTS.SETTINGS(tenantId),
      {
        cacheKey: `tenant_settings_${tenantId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async updateTenantSettings(tenantId: ID, settings: any): Promise<ApiResponse<any>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_settings_${tenantId}`);
    
    return apiClient.customPut<any>(
      ENDPOINTS.TENANTS.SETTINGS(tenantId),
      settings
    );
  }
  
  async resetTenantSettings(tenantId: ID): Promise<ApiResponse<any>> {
    // Cache'i temizle
    apiClient.clearCache(`tenant_settings_${tenantId}`);
    
    return apiClient.customPost<any>(
      `${ENDPOINTS.TENANTS.SETTINGS(tenantId)}/reset`,
      {}
    );
  }
  
  // ==================== EXPORT OPERATIONS ====================
  
  async exportTenantData(tenantId: ID, format: ExportFormat, filters?: any): Promise<Blob> {
    const exportData = {
      format,
      filters,
      tenantId
    };
    
    return apiClient.exportData(
      ENDPOINTS.TENANTS.BASE,
      format,
      exportData
    );
  }
  
  async exportTenantReport(tenantId: ID, format: ExportFormat): Promise<Blob> {
    return apiClient.exportData(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/export-report`,
      format
    );
  }
  
  // ==================== UTILITY METHODS ====================
  
  async searchTenants(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<TenantResponse>> {
    const searchParams = {
      ...params,
      search: query
    };
    
    return apiClient.getList<TenantResponse>(
      `${ENDPOINTS.TENANTS.BASE}/search`,
      searchParams,
      {
        cacheKey: `tenant_search_${query}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async validateTenantDomain(domain: string): Promise<ApiResponse<{ valid: boolean; message?: string }>> {
    return apiClient.customGet<{ valid: boolean; message?: string }>(
      `${ENDPOINTS.TENANTS.BASE}/validate-domain`,
      {
        params: { domain },
        cacheKey: `tenant_domain_validate_${domain}`,
        cacheTTL: 600000 // 10 dakika cache
      }
    );
  }
  
  async checkTenantAvailability(name: string, domain: string): Promise<ApiResponse<{ available: boolean; suggestions?: string[] }>> {
    return apiClient.customGet<{ available: boolean; suggestions?: string[] }>(
      `${ENDPOINTS.TENANTS.BASE}/check-availability`,
      {
        params: { name, domain },
        cacheKey: `tenant_availability_${name}_${domain}`,
        cacheTTL: 600000 // 10 dakika cache
      }
    );
  }
  
  // ==================== ADDITIONAL METHODS ====================
  
  /**
   * Get tenant's fleets
   */
  async getTenantFleets(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    return apiClient.getList<any>(
      ENDPOINTS.TENANTS.FLEETS(tenantId),
      params,
      {
        cacheKey: `tenant_fleets_${tenantId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get tenant's buildings
   */
  async getTenantBuildings(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    return apiClient.getList<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/buildings`,
      params,
      {
        cacheKey: `tenant_buildings_${tenantId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get tenant's units
   */
  async getTenantUnits(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    return apiClient.getList<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/units`,
      params,
      {
        cacheKey: `tenant_units_${tenantId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get tenant's activity log
   */
  async getTenantActivityLog(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    return apiClient.getList<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/activity-log`,
      params,
      {
        cacheKey: `tenant_activity_${tenantId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get tenant's API usage
   */
  async getTenantApiUsage(tenantId: ID, period: string): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/api-usage`,
      {
        params: { period },
        cacheKey: `tenant_api_usage_${tenantId}_${period}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get tenant's storage usage
   */
  async getTenantStorageUsage(tenantId: ID): Promise<ApiResponse<{ used: number; limit: number; percentage: number }>> {
    return apiClient.customGet<{ used: number; limit: number; percentage: number }>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/storage-usage`,
      {
        cacheKey: `tenant_storage_${tenantId}_${Date.now()}`,
        cacheTTL: 60000 // 1 dakika cache (real-time data)
      }
    );
  }
  
  /**
   * Get tenant's compliance status
   */
  async getTenantComplianceStatus(tenantId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/compliance`,
      {
        cacheKey: `tenant_compliance_${tenantId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Send tenant notification
   */
  async sendTenantNotification(tenantId: ID, notification: any): Promise<ApiResponse<void>> {
    return apiClient.customPost<void>(
      `${ENDPOINTS.TENANTS.BASE}/${tenantId}/notify`,
      notification
    );
  }
}

// Singleton instance
export const tenantService = new TenantService();

// Default export
export default tenantService; // BU SATIR ÖNEMLİ: default export ekledim
export { TenantService };